import brand from '../shell/brand';

// Being told when Quadrant is not the window in front.
//
// The toast lives inside the page, so an operator who has switched to the weighbridge software, to
// another tab, or away from the machine learns nothing until they come back. The browser's own
// notifications cross that boundary: the operating system draws them over whatever is in front. So
// an urgent alert raised while this tab is in the background is handed over there instead, and the
// toast is kept for when somebody is actually looking at the page.
//
// Permission is the browser's to give and is asked for only when somebody turns this on, because a
// permission prompt thrown at a page on load is the thing people refuse by reflex. The choice is
// remembered per account on this computer: it describes this desk, not the plant.

const key = (user) => `desktop-alerts.${user?.tenantId || 'unknown'}.${user?.id || 'anonymous'}`;

export const supported = () => typeof window !== 'undefined' && 'Notification' in window;

export const permission = () => (supported() ? window.Notification.permission : 'unsupported');

export const chosen = (user) => {
  try {
    return window.localStorage.getItem(key(user)) === 'on';
  } catch (error) {
    return false;
  }
};

export const remember = (user, on) => {
  try {
    window.localStorage.setItem(key(user), on ? 'on' : 'off');
  } catch (error) {
    // A browser that refuses storage simply forgets the choice at the end of the session.
  }
};

// ---- and being woken when the app is not running at all -------------------------------------------
//
// Everything above reaches somebody whose browser is running this app, in front or behind. A phone
// with the app closed is reached only through its own push service, which holds a subscription and
// wakes the service worker. That is a separate thing to ask the browser for, and it is asked for in
// the same breath as the permission, because turning alerts on and then not being alerted is not a
// distinction anybody wants explained to them.
//
// Best effort, always. A browser without push, a deployment with no keys configured, a network that
// drops: none of them should stop the alerts that do work from working.

const binaryKey = (base64) => {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(padded);
  return Uint8Array.from([...raw].map((character) => character.charCodeAt(0)));
};

const pushable = () => typeof window !== 'undefined'
  && 'serviceWorker' in window.navigator && 'PushManager' in window;

export const subscribe = async () => {
  if (!pushable()) return false;
  try {
    const answer = await fetch('/api/push/key', { credentials: 'same-origin' });
    const { key } = answer.ok ? await answer.json() : {};
    // A deployment with no keys cannot send, and subscribing to nothing would leave a device
    // waiting for a push that is never coming.
    if (!key) return false;
    const registration = await window.navigator.serviceWorker.ready;
    const already = await registration.pushManager.getSubscription();
    const subscription = already || await registration.pushManager.subscribe({
      // The bargain: a push always shows the person something. The worker keeps it.
      userVisibleOnly: true,
      applicationServerKey: binaryKey(key),
    });
    await fetch('/api/push/subscriptions', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint: subscription.endpoint }),
    });
    return true;
  } catch (error) {
    return false;
  }
};

export const unsubscribe = async () => {
  if (!pushable()) return;
  try {
    const registration = await window.navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;
    // Told here first: a subscription dropped without a word leaves this deployment pushing at an
    // address that answers 410 for every alert until something notices.
    await fetch('/api/push/subscriptions/forget', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint: subscription.endpoint }),
    });
    await subscription.unsubscribe();
  } catch (error) {
    // The device simply keeps a subscription nobody sends to, which costs nothing.
  }
};

// Turning it on. Returns the permission the browser ended up at, so the control can say what
// happened - granted, refused, or a browser that has no such thing.
export const enable = async (user) => {
  if (!supported()) return 'unsupported';
  let state = permission();
  if (state === 'default') {
    try {
      state = await window.Notification.requestPermission();
    } catch (error) {
      state = permission();
    }
  }
  remember(user, state === 'granted');
  if (state === 'granted') await subscribe();
  return state;
};

export const disable = (user) => {
  remember(user, false);
  unsubscribe();
};

export const enabled = (user) => supported() && permission() === 'granted' && chosen(user);

// One notification at a time per desk. The tag means a second urgent alert replaces the first
// rather than stacking a column of them over somebody else's work, and a critical one is left on
// screen until it is dealt with, the same rule the in-page toast follows.
export const notify = ({
  title, body, critical, onClick,
}) => {
  if (!supported() || permission() !== 'granted') return null;
  try {
    const note = new window.Notification(title, { body, tag: `${brand.key}-alerts`, requireInteraction: !!critical });
    note.onclick = () => {
      try {
        window.focus();
      } catch (error) {
        // Some browsers refuse to raise the window; the click still opens the alert below.
      }
      note.close();
      if (onClick) onClick();
    };
    return note;
  } catch (error) {
    return null;
  }
};
